/**
 * 用户状态管理 Store
 * 
 * 功能：
 * - 管理用户登录状态和Token
 * - 管理用户信息和权限
 * - 处理登录、退出等认证流程
 * - Token过期检测和刷新
 * 
 * @module store/user
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import i18n from '@/locales'  // 假设 i18n 实例从此导出
import authApi from '@/api/auth'

import { storeToken, clearToken, getToken } from '@/utils/crypto'

const { t } = i18n.global

export const useUserStore = defineStore('user', () => {
  // ============ 状态定义 ============
  /** @type {Ref<string>} 用户Token */
  const token = ref(localStorage.getItem('accessToken') || '')
  /** @type {Ref<Object>} 用户信息 */
  const userInfo = ref(JSON.parse(localStorage.getItem('userInfo') || '{}'))
  /** @type {Ref<Array>} 用户权限列表 */
  const permissions = ref(JSON.parse(localStorage.getItem('permissions') || '[]'))

  // ============ 计算属性 ============
  /**
   * 用户登录状态
   * @type {ComputedRef<boolean>}
   */
  const isLoggedIn = computed(() => {
    const currentToken = token.value || localStorage.getItem('accessToken') || ''
    if (!currentToken) return false

    try {
      const tokenData = getToken()
      return !!(tokenData && tokenData.accessToken && !tokenData.isExpired)
    } catch (error) {
      return false
    }
  })

  /** @type {ComputedRef<Object>} 当前用户信息 */
  const currentUser = computed(() => userInfo.value)
  /** @type {ComputedRef<Array>} 当前用户权限列表 */
  const userPermissions = computed(() => permissions.value)

  // ============ 认证方法 ============
  /**
   * 用户登录
   * @param {Object} loginParams - 登录参数
   * @returns {Promise<Object>} 返回登录结果
   */
  async function login(loginParams) {
    try {
      const response = await authApi.login(loginParams)
      console.log('登录接口响应:', response)

      if (response && response.token) {
        // 确保token有Bearer前缀
        const tokenWithBearer = response.token.startsWith('Bearer ')
          ? response.token
          : `Bearer ${response.token}`

        // 存储token到本地存储
        storeToken({
          token: tokenWithBearer,
          expires_in: 24 * 60 * 60,
          token_type: 'Bearer',
          theme: response.theme || 'classic',
          isSaas: response.isSaas || false
        })

        // 更新响应式状态
        token.value = tokenWithBearer

        // 获取用户信息
        await fetchUserInfo()

        return {
          success: true,
          message: response.message || t('login.loginSuccess'),
          data: response
        }
      } else {
        // 处理成功响应但没有token的情况
        return {
          success: false,
          message: response?.msg || response?.message || t('login.loginError'),
          data: response,
          status: 200  // 业务逻辑失败，但HTTP状态是成功的
        }
      }
    } catch (error) {
      console.error('登录请求失败:', error)

      // 处理HTTP错误
      let errorMessage = t('login.loginError')
      let errorData = null
      let statusCode = 500

      if (error.response) {
        // HTTP错误响应（400, 401, 500等）
        const { status, data } = error.response
        statusCode = status
        errorData = data

        // 提取具体的错误信息
        if (status === 400) {
          errorMessage = data?.msg || data?.message || data?.error || t('login.paramsError')
        } else if (status === 401) {
          errorMessage = data?.msg || data?.message || t('login.credentialsError')
        } else if (status === 429) {
          errorMessage = t('login.timeoutError')
        } else if (status === 403) {
          errorMessage = t('login.disabledError')
        } else if (status === 404) {
          errorMessage = t('login.endpointError')
        } else if (status >= 500) {
          errorMessage = data?.msg || data?.message || t('login.serverError')
        } else {
          errorMessage = data?.msg || data?.message || t('login.loginError') + ` (${status})`
        }
      } else if (error.request) {
        // 请求已发送但没有收到响应
        errorMessage = t('login.networkError')
      } else {
        // 其他错误
        errorMessage = error.message || t('login.loginRequestFailed')
      }

      return {
        success: false,
        message: errorMessage,
        data: errorData,
        status: statusCode,
        error: error  // 保留原始错误对象，便于调试
      }
    }
  }

  /**
   * 用户退出登录
   * 调用后端退出接口并清除本地数据
   * @returns {Promise<Object>} 返回退出结果
   */
  async function logout() {
    try {
      const storedToken = getToken()

      if (storedToken.accessToken && !storedToken.isExpired) {
        // 同步调用退出接口，等待结果
        const result = await authApi.logout()

        // 只有 code === 200 时才认为是成功
        if (result && result.code === 200) {
          // 只在接口返回200成功时才清除本地数据
          clearUserData(false) // 不跳转到登录页

          return {
            success: true,
            message: result.msg || t('login.logoutSuccess'),
            code: result.code
          }
        } else {
          console.warn('退出接口返回失败状态', result)

          // 接口返回非200（如500），不清除本地数据
          return {
            success: false,
            message: result?.msg || t('login.logoutFailed'),
            code: result?.code || 500
          }
        }
      } else {
        // Token无效时，仍然可以清除本地数据
        clearUserData(false)

        return {
          success: true,
          message: t('login.tokenExpired')
        }
      }
    } catch (error) {
      console.error('退出登录过程中出错:', error)

      // 接口调用失败（网络错误等），不清除本地数据
      let errorMessage = t('login.logoutError')
      let errorCode = 500

      if (error.response?.data?.msg) {
        errorMessage = error.response.data.msg
        errorCode = error.response.data.code || error.response.status
      } else if (error.message) {
        errorMessage = error.message
      }

      // 特别注意：这里不清除本地数据！！！
      return {
        success: false,
        message: errorMessage,
        code: errorCode
      }
    }
  }

  // 清除用户数据
  function clearUserData(redirectToLogin = false) {
    // 清除 store 状态
    token.value = ''
    userInfo.value = {}
    permissions.value = []

    // 清除 token 相关存储
    clearToken()

    // 清除其他本地存储
    const itemsToRemove = [
      'userInfo',
      'permissions',
      'accessToken',
      'expiresIn',
      'isSaas',
      'language',
      'locale',
      'tenantId',
      'theme',
      'tokenType',
      'userId'
    ]

    itemsToRemove.forEach(item => {
      localStorage.removeItem(item)
      sessionStorage.removeItem(item)
    })

    // 只有参数指定时才跳转到登录页
    if (redirectToLogin && window.location.pathname !== '/login') {
      setTimeout(() => {
        window.location.href = '/login'
      }, 100)
    }
  }

  // 获取用户信息
  async function fetchUserInfo() {
    const response = await authApi.getCurrentUser()

    if (response && response.userId) {
      userInfo.value = {
        userId: response.userId || '',
        userAccount: response.userAccount || '',
        userName: response.userName || '',
        name: response.userName || '',
        tenantId: response.tenantId || 'default',
        isAdministrator: response.isAdministrator || false
      }

      // 保存到本地存储
      localStorage.setItem('userInfo', JSON.stringify(userInfo.value))
      return userInfo.value
    } else {
      throw new Error(response?.msg || t('login.fetchUserFailed'))
    }
  }

  // 刷新用户信息
  async function refreshUserInfo() {
    try {
      const userData = await fetchUserInfo()
      return {
        success: true,
        data: userData,
        message: t('login.userInfoUpdated')
      }
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || t('login.fetchUserError')
      }
    }
  }

  // 刷新token
  async function refreshTokenIfNeeded() {
    const storedToken = getToken()
    return !storedToken.isExpired
  }

  // 检查并更新用户信息
  async function checkAndUpdateUser() {
    if (isLoggedIn.value) {
      const tokenValid = await refreshTokenIfNeeded()
      if (tokenValid) {
        // 如果用户信息不完整，尝试获取
        if (!userInfo.value.userName || !userInfo.value.userAccount) {
          try {
            await fetchUserInfo()
          } catch (error) {
            // 静默失败
          }
        }
        return true
      } else {
        clearUserData(true) // 跳转到登录页
        return false
      }
    }
    return false
  }

  // 初始化检查用户信息
  async function initUserInfo() {
    if (isLoggedIn.value) {
      return await checkAndUpdateUser()
    }
    return false
  }

  return {
    // 状态
    token,
    userInfo,
    permissions,

    // 计算属性
    isLoggedIn,
    currentUser,
    userPermissions,

    // 主要方法
    login,
    logout,
    fetchUserInfo,
    refreshUserInfo,
    clearUserData,
    checkAndUpdateUser,
    initUserInfo,

    // 辅助方法
    refreshTokenIfNeeded
  }
})
